/**
 * Fetch every page of a paginated list endpoint and return the flattened items.
 *
 * The orders list endpoint caps `per_page` at 100 (apps/api-python .../endpoints/orders.py),
 * so any view that reads only page 1 silently truncates clients with more than
 * 100 records - the marketing plan and orders tabs were undercounting filled
 * sales for every client with a large order history. This walks the remaining
 * pages using the `total` reported by the first response so nothing is dropped.
 */

export interface PageResult<T> {
  items: T[];
  total: number;
}

/**
 * Repeatedly calls `fetchPage(page, perPage)` starting at page 1 and continues
 * until every page implied by the reported `total` has been retrieved.
 *
 * Pages are fetched sequentially so a client with hundreds of records issues a
 * bounded, ordered series of requests rather than a burst. The first response's
 * `total` decides how many pages exist; if it is missing/non-finite we fall back
 * to the items already returned (single page).
 */
export async function fetchAllPages<T>(
  fetchPage: (page: number, perPage: number) => Promise<PageResult<T>>,
  perPage = 100,
): Promise<T[]> {
  const size = perPage > 0 ? perPage : 100;
  const first = await fetchPage(1, size);
  const all: T[] = [...first.items];

  const total = Number.isFinite(first.total) ? first.total : all.length;
  const pages = Math.ceil(total / size);

  for (let page = 2; page <= pages; page++) {
    const next = await fetchPage(page, size);
    // Stop early if the server runs out of rows before `total` implies. Guards
    // against an over-reported total or a smaller server-side page size, either
    // of which would otherwise spin extra empty requests.
    if (next.items.length === 0) break;
    all.push(...next.items);
  }

  return all;
}
